<template>
    <div class="border-t borger-dove-gray-100 w-full py-4">
        <ais-panel>
            <template v-slot:header>
                <div class="flex justify-between items-center w-full" :class="{ 'pb-4': isOpen }">
                    <div class="flex gap-2 items-center">
                        <div @click="isOpen = !isOpen" class="flex-1 cursor-pointer">
                            <span class="font-bold">{{ label }}</span>
                        </div>
                        <button v-if="description" @click="showModal = true"
                            class="p-1 rounded-full transition-colors mr-2" aria-label="More information">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="w-5 h-5 text-gray-600">
                                <path
                                    d="M320 96C443.7 96 544 196.3 544 320C544 443.7 443.7 544 320 544C196.3 544 96 443.7 96 320C96 196.3 196.3 96 320 96zM320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM272 416C263.2 416 256 423.2 256 432C256 440.8 263.2 448 272 448L368 448C376.8 448 384 440.8 384 432C384 423.2 376.8 416 368 416L336 416L336 304C336 295.2 328.8 288 320 288L272 288C263.2 288 256 295.2 256 304C256 312.8 263.2 320 272 320L304 320L304 416L272 416zM320 248C333.3 248 344 237.3 344 224C344 210.7 333.3 200 320 200C306.7 200 296 210.7 296 224C296 237.3 306.7 248 320 248z" />
                            </svg>
                        </button>
                    </div>
                    <div @click="isOpen = !isOpen" data-caret-icon class="cursor-pointer pr-4">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true"
                            class="h-4 w-4 transition-transform duration-300 ease-in-out"
                            :class="{ 'rotate-180': isOpen }">
                            <path
                                d="M241 337c-9.4 9.4-24.6 9.4-33.9 0L47 177c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l143 143L367 143c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9L241 337z" />
                        </svg>
                    </div>
                </div>
            </template>
            <transition enter-from-class="opacity-0" leave-to-class="opacity-0"
                enter-active-class="transition duration-100" leave-active-class="transition duration-100">
                <template v-if="isOpen" class="max-h-screen overflow-hidden">
                    <ais-refinement-list :attribute="props.attribute" :sort-by="['name:asc']" searchable :limit="150">
                        <template v-slot="{
                            items,
                            refine,
                            createURL,
                            searchForItems,
                        }">
                            <input v-if="searchForItems == true" @input="searchForItems($event.currentTarget.value)">
                            <ul>
                                <li v-for="(item) in processedItems(items).slice(0, itemsToShow)" :key="item.value"
                                    class="flex items-center gap-2 pb-2">
                                    <input type="checkbox" :checked="item.isRefined" @change="refine(item.value)">
                                    <a :href="createURL(item)" :style="{ fontWeight: item.isRefined ? 'bold' : '' }"
                                        @click.prevent="refine(item.value)" class="flex gap-2">
                                        <span v-html="item.value" class="text-base" />
                                        <span
                                            class="text-sm text-gray-700 border border-dove-gray-100 rounded-full px-2">
                                            {{ item.count.toLocaleString() }}
                                        </span>
                                    </a>
                                </li>
                            </ul>
                            <button v-if="itemsToShow < items.length" @click="showMore(items)"
                                class="underline text-sm text-gray-900">
                                {{ $t('message.typesense.panel.showMore', { quantity: items.length - itemsToShow }) }}
                            </button>
                            <button v-else-if="itemsToShow > 7" @click="showLess"
                                class="underline text-sm text-gray-900">
                                {{ $t('message.typesense.panel.showLess') }}
                            </button>
                        </template>
                    </ais-refinement-list>
                </template>
            </transition>
        </ais-panel>
        <!-- Modal -->
        <transition name="transition-fade">
            <div v-if="showModal" role="dialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                @click="showModal = false">
                <div class="bg-white rounded-lg max-w-md w-full relative overflow-hidden divide-y divide-gray-200 z-50"
                    @click.stop>
                    <div class="flex justify-between items-center p-6 pb-4">
                        <h3 class="text-lg font-semibold text-gray-900">About {{ label }}</h3>
                        <button @click="showModal = false" aria-label="Close">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div class="px-6 py-4" v-html="props.description"></div>
                    <div class="p-4 pt-3">
                        <button @click="showModal = false" class="w-full btn btn--primary">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { defineEmits } from 'vue'

const future = { preserveSharedStateOnUnmount: true };
import { ref, defineProps } from 'vue'
import { AisInstantSearch, AisCurrentRefinements, AisAutocomplete, AisSearchBox, AisSortBy, AisToggleRefinement, AisConfigure, AisPanel, AisHits, AisHighlight, AisRefinementList, AisPagination, AisHitsPerPage } from 'vue-instantsearch/vue3/es';
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
const itemsToShow = ref(7)
const isOpen = ref(true);
const showModal = ref(false);

const props = defineProps({
    attribute: {
        type: String,
        required: true
    },
    label: {
        type: String,
        required: true
    },
    description: {
        type: String,
        required: false
    }
})

const sortingOrders = {
    frame_width: {
        "Narrow": 1,
        "Medium": 2,
        "Wide": 3,
        "Extra Wide": 4
    },
    rabbet_depth: {
        "Shallow": 1,
        "Medium": 2,
        "Deep": 3,
        "Extra Deep": 4
    }
};

// Only applies sorting for 'frame_width' and 'rabbet_depth'
const processedItems = (items) => {
    if (
        props.attribute === 'frame_type' ||
        props.attribute === 'material' ||
        props.attribute === 'color_family' ||
        props.attribute === 'finish' ||
        props.attribute === 'substrates' ||
        props.attribute === 'art_sizes'
    ) {
        return [...items].sort((a, b) => b.count - a.count);
    }

    if (props.attribute === 'color' || props.attribute === 'decor_style') {
        if (items.length > 7 && itemsToShow.value === items.length) {
            return [...items].sort((a, b) => a.label.localeCompare(b.label));
        } else {
            return [...items].sort((a, b) => b.count - a.count);
        }
    }
    const order = sortingOrders[props.attribute];
    if (!order) return items;

    return [...items].sort((a, b) => {
        const nameA = a.label.split(":")[0].trim();
        const nameB = b.label.split(":")[0].trim();
        return (order[nameA] || 99) - (order[nameB] || 99);
    });
};

const showMore = (items) => {
    itemsToShow.value = items.length;
};

const showLess = () => {
    itemsToShow.value = 7;
};
</script>